<script setup lang="ts">

import {computed, PropType, inject, ref, watch} from "vue";
import {injectKeyFormConfig, injectKeyFormFieldLayout} from "/@src/constants/keys";
import {getNestedField} from "/@src/utils";

export type FormFieldLayout = 'section'|'field'

export type FormFieldSize = 'wrap'|'full'
export interface AddonInputConfig {
  placeholder?:string
  icon?:string
  type?:string
}
export interface AddonSelectConfig {
  placeholder?:string
  options?:Array<{label:string,value:any}>
  defaultVal?:any
}

export interface AddonButtonConfig {
  text?:string
  onClick?:()=>void
}

export interface AddonSwitcherConfig {
  label?:string
  handler?:(model:any,field:string,index?:number)=>boolean
  onChange?:(value:boolean,modelValue:any,field:string,index?:number)=>void
}

export interface AddonConfig {
  field?:string
  label?:string
  input?:AddonInputConfig
  select?:AddonSelectConfig
  button?:AddonButtonConfig
  switcher?:AddonSwitcherConfig
}
export interface FormFieldMenu {
  name: string
  icon?: string
  action?: (model?:any,index?:number) => void
}

const props = defineProps({
  label: String,
  field: String,
  index: Number,
  layout: {
    type: String as PropType<FormFieldLayout>,
  },
  labelHighlight: {
    type: Boolean,
    default: false,
  },
  menus:{
    type: Array as PropType<FormFieldMenu[]>,
  },
  width:{
    type: String as PropType<FormFieldSize>,
    default: 'full'
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  addonConfig:{
    type: Object as PropType<AddonConfig>,
  },
  modelValue:{
    type: [String,Number,Boolean,Object,Array],
  }
})

const showRequired = computed(()=>{
  return props.required && !props.disabled
})

const config = inject(injectKeyFormConfig)
const defaultLayout = inject(injectKeyFormFieldLayout)
const addonModel = ref()
const isLabelHighlight = computed(()=>{
  return props.labelHighlight || config?.value?.labelHighlight
})

const layout = computed(()=>{
  return props.layout || defaultLayout
})

const update = (formData:any) => {
  if (props.addonConfig){
    if (props.addonConfig.field){
      let data
      if (Array.isArray(formData)) {
        data = getNestedField(formData[props.index??0], props.addonConfig.field)
      } else {
        data = getNestedField(formData,props.addonConfig.field)
      }
      addonModel.value = props.addonConfig.switcher?.handler?.(data,props.field!,props.index)??false
    }
  }
}
if (props.addonConfig) {
  watch(()=>props.modelValue,(newVal)=>{
    addonModel.value = props.addonConfig?.switcher?.handler?.(newVal,props.field!,props.index)??false
  },{immediate:true})
  watch(addonModel,(newVal)=>{
    props.addonConfig?.switcher?.onChange?.(newVal,props.modelValue,props.addonConfig?.field!,props.index)
  })
}

const handleClickMenu = (menu:FormFieldMenu) => {
  menu?.action?.(props.modelValue,props.index)
}

defineExpose({
  update,
})
</script>

<template>
  <div v-if="layout === 'field'" class="field is-horizontal">
    <div class="field-label is-normal">
      <label class="label" :class="[isLabelHighlight&&'has-text-primary']">{{label}}</label>
    </div>
    <div class="field-body">
      <slot></slot>
    </div>
  </div>
  <!-- 尝试撑满item宽，可能会变动 -->
  <div v-else  :class="[width==='full'&&'flex-1']">
      <div class="flex items-center gap-3">
        <label class="mb-1 line-clamp-1" :class="[isLabelHighlight&&'has-text-primary']">{{label}}</label>
        <VSwitchSegment
            v-if="addonConfig?.switcher"
            v-model="addonModel"
            class="ml-auto"
            :label-true="addonConfig?.label"
            color="primary"
            @on-change="value => addonConfig?.switcher?.onChange?.(value,modelValue,addonConfig?.field!,index)"
        />
        <label v-if="showRequired" class="label !text-red-400" :class="[menus&&'ml-2',!menus&&'ml-auto']">必填</label>
        <div v-if="menus" class="flex items-center gap-3 ml-auto">
          <div class="has-text-primary line-clamp-1 cursor-pointer font-medium flex items-center gap-1" v-for="menu in menus"
               @click="handleClickMenu(menu)">
            <i
                v-if="menu.icon&& menu.icon.indexOf(':') !== -1"
                aria-hidden="true"
                class="iconify"
                :data-icon="menu.icon"
            ></i>
            <i v-else-if="menu.icon" aria-hidden="true" :class="menu.icon"></i>{{menu.name}}
          </div>
        </div>
      </div>
      <slot></slot>
  </div>
</template>
<style lang="scss">
//.addon{
//  .slider::after{
//    width:12px;
//    height:12px;
//  }
//}
</style>
